import { FX_HALF, FX_ONE, FX_SHIFT } from '../../engine/simulation/fixed';

/**
 * Grid A* pathfinder with octile heuristic, deterministic tie-breaking,
 * closest-reachable fallback for blocked goals, and string-pulling smoothing.
 *
 * Costs are integers (10 orthogonal, 14 diagonal) so results are identical on
 * every engine. Search state arrays are reused between queries (no per-query
 * allocation of grid-sized buffers).
 */
export class Pathfinder {
  readonly size: number;
  private g: Int32Array;
  private parent: Int32Array;
  private gen: Uint32Array;
  private closedGen: Uint32Array;
  private curGen = 0;
  private heap: Int32Array;
  private heapF: Int32Array;
  private heapLen = 0;
  /** Nodes expanded by the most recent query (for budgeting/benchmarks). */
  lastExpanded = 0;
  /** True when the last query proved the goal unreachable (open list exhausted). */
  lastUnreachable = false;

  /** Combined static + dynamic block grid (1 = blocked), owned by the World. */
  private grid: Uint8Array;

  constructor(size: number, grid: Uint8Array) {
    this.grid = grid;
    this.size = size;
    const n = size * size;
    this.g = new Int32Array(n);
    this.parent = new Int32Array(n);
    this.gen = new Uint32Array(n);
    this.closedGen = new Uint32Array(n);
    this.heap = new Int32Array(n * 2);
    this.heapF = new Int32Array(n * 2);
  }

  private push(node: number, f: number): void {
    let i = this.heapLen++;
    const h = this.heap;
    const hf = this.heapF;
    while (i > 0) {
      const p = (i - 1) >> 1;
      // Tie-break on node index for determinism.
      if (hf[p] < f || (hf[p] === f && h[p] <= node)) break;
      h[i] = h[p];
      hf[i] = hf[p];
      i = p;
    }
    h[i] = node;
    hf[i] = f;
  }

  private pop(): number {
    const h = this.heap;
    const hf = this.heapF;
    const top = h[0];
    const n = --this.heapLen;
    if (n > 0) {
      const node = h[n];
      const f = hf[n];
      let i = 0;
      for (;;) {
        let c = 2 * i + 1;
        if (c >= n) break;
        if (c + 1 < n && (hf[c + 1] < hf[c] || (hf[c + 1] === hf[c] && h[c + 1] < h[c]))) c++;
        if (hf[c] > f || (hf[c] === f && h[c] >= node)) break;
        h[i] = h[c];
        hf[i] = hf[c];
        i = c;
      }
      h[i] = node;
      hf[i] = f;
    }
    return top;
  }

  private heuristic(x: number, y: number, gx: number, gy: number): number {
    const dx = Math.abs(x - gx);
    const dy = Math.abs(y - gy);
    return dx > dy ? 14 * dy + 10 * (dx - dy) : 14 * dx + 10 * (dy - dx);
  }

  isBlockedTile(tx: number, ty: number): boolean {
    if (tx < 0 || ty < 0 || tx >= this.size || ty >= this.size) return true;
    return this.grid[ty * this.size + tx] !== 0;
  }

  /** Straight-line walkability between two fixed-point points. */
  lineClear(ax: number, ay: number, bx: number, by: number): boolean {
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.max(Math.abs(dx), Math.abs(dy));
    const steps = Math.max(1, Math.ceil(len / (FX_ONE >> 2)));
    let lastTile = -1;
    for (let s = 0; s <= steps; s++) {
      const x = ax + Math.trunc((dx * s) / steps);
      const y = ay + Math.trunc((dy * s) / steps);
      const tx = x >> FX_SHIFT;
      const ty = y >> FX_SHIFT;
      const t = ty * this.size + tx;
      if (t === lastTile) continue;
      lastTile = t;
      if (this.isBlockedTile(tx, ty)) return false;
    }
    return true;
  }

  /** Find nearest unblocked tile to (tx,ty) within radius, or -1. */
  nearestOpen(tx: number, ty: number, maxR = 8): number {
    if (!this.isBlockedTile(tx, ty)) return ty * this.size + tx;
    for (let r = 1; r <= maxR; r++) {
      let best = -1;
      let bestD = 1 << 30;
      for (let dy = -r; dy <= r; dy++)
        for (let dx = -r; dx <= r; dx++) {
          if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
          const x = tx + dx;
          const y = ty + dy;
          if (this.isBlockedTile(x, y)) continue;
          const d = dx * dx + dy * dy;
          const idx = y * this.size + x;
          if (d < bestD || (d === bestD && idx < best)) {
            bestD = d;
            best = idx;
          }
        }
      if (best >= 0) return best;
    }
    return -1;
  }

  /**
   * Compute a path from fixed-point start to goal. Returns a flat array of
   * fixed-point waypoints [x0,y0,x1,y1,...] excluding the start. If the goal is
   * unreachable the path leads to the closest reachable tile.
   */
  findPath(sx: number, sy: number, gx: number, gy: number, maxExpand = 6000, rect: number[] | null = null): number[] {
    const goalRange = rect ? 1 : 0;
    const grid = this.grid;
    this.lastUnreachable = false;
    const size = this.size;
    let stx = sx >> FX_SHIFT;
    let sty = sy >> FX_SHIFT;
    if (this.isBlockedTile(stx, sty)) {
      const o = this.nearestOpen(stx, sty, 3);
      if (o < 0) return [];
      stx = o % size;
      sty = (o / size) | 0;
    }
    const gtx0 = Math.min(size - 1, Math.max(0, gx >> FX_SHIFT));
    const gty0 = Math.min(size - 1, Math.max(0, gy >> FX_SHIFT));
    let goalExact = true;
    let gtx = gtx0;
    let gty = gty0;
    if (goalRange > 0) {
      // Already adjacent to the target region.
      if (stx >= rect![0] - 1 && stx <= rect![2] + 1 && sty >= rect![1] - 1 && sty <= rect![3] + 1) {
        this.lastExpanded = 0;
        return this.finalApproach([], sx, sy, rect!);
      }
      goalExact = false;
    } else if (this.isBlockedTile(gtx, gty)) {
      goalExact = false;
      const o = this.nearestOpen(gtx, gty, 6);
      if (o >= 0) {
        gtx = o % size;
        gty = (o / size) | 0;
      }
    }

    if (goalRange === 0 && this.lineClear(sx, sy, goalExact ? gx : (gtx << FX_SHIFT) + FX_HALF, goalExact ? gy : (gty << FX_SHIFT) + FX_HALF)) {
      this.lastExpanded = 0;
      return goalExact ? [gx, gy] : [(gtx << FX_SHIFT) + FX_HALF, (gty << FX_SHIFT) + FX_HALF];
    }

    const gen = ++this.curGen;
    const start = sty * size + stx;
    const goal = gty * size + gtx;
    this.heapLen = 0;
    this.g[start] = 0;
    this.gen[start] = gen;
    this.parent[start] = -1;
    this.push(start, this.heuristic(stx, sty, gtx, gty));
    let best = start;
    let bestH = this.heuristic(stx, sty, gtx, gty);
    let expanded = 0;
    let found = false;

    while (this.heapLen > 0) {
      const cur = this.pop();
      if (this.closedGen[cur] === gen) continue;
      this.closedGen[cur] = gen;
      if (cur === goal) {
        found = true;
        break;
      }
      if (goalRange > 0) {
        const ccx = cur % size;
        const ccy = (cur / size) | 0;
        if (ccx >= rect![0] - 1 && ccx <= rect![2] + 1 && ccy >= rect![1] - 1 && ccy <= rect![3] + 1) {
          found = true;
          best = cur;
          break;
        }
      }
      if (++expanded > maxExpand) break;
      const cx = cur % size;
      const cy = (cur / size) | 0;
      const hcur = this.heuristic(cx, cy, gtx, gty);
      if (hcur < bestH || (hcur === bestH && cur < best)) {
        bestH = hcur;
        best = cur;
      }
      const gc = this.g[cur];
      for (let d = 0; d < 8; d++) {
        const dx = DX[d];
        const dy = DY[d];
        const nx = cx + dx;
        const ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= size || ny >= size) continue;
        const ni = ny * size + nx;
        if (grid[ni] !== 0) continue;
        if (dx !== 0 && dy !== 0) {
          // No corner cutting.
          if (grid[cy * size + nx] !== 0 || grid[ny * size + cx] !== 0) continue;
        }
        if (this.closedGen[ni] === gen) continue;
        const ng = gc + (dx !== 0 && dy !== 0 ? 14 : 10);
        if (this.gen[ni] === gen && this.g[ni] <= ng) continue;
        this.gen[ni] = gen;
        this.g[ni] = ng;
        this.parent[ni] = cur;
        this.push(ni, ng + ((this.heuristic(nx, ny, gtx, gty) * 5) >> 2));
      }
    }
    this.lastExpanded = expanded;
    if (!found && this.heapLen === 0 && expanded <= maxExpand) this.lastUnreachable = true;

    const end = found ? (goalRange > 0 ? best : goal) : best;
    const tiles: number[] = [];
    for (let n = end; n !== -1 && n !== start; n = this.parent[n]) tiles.push(n);
    tiles.reverse();
    if (tiles.length === 0) return rect && found ? this.finalApproach([], sx, sy, rect) : [];

    // String-pulling: keep only waypoints needed to maintain line of sight.
    const pts: number[] = [];
    for (const t of tiles) pts.push(((t % size) << FX_SHIFT) + FX_HALF, (((t / size) | 0) << FX_SHIFT) + FX_HALF);
    if (found && goalExact) {
      pts[pts.length - 2] = gx;
      pts[pts.length - 1] = gy;
    }
    const out: number[] = [];
    let ax = sx;
    let ay = sy;
    const res = this.smooth(pts, out, ax, ay);
    // Footprint goals: the final waypoint hugs the target's edge.
    return rect && found ? this.finalApproach(res, sx, sy, rect) : res;
  }

  private smooth(pts: number[], out: number[], ax: number, ay: number): number[] {
    let i = 0;
    const n = pts.length / 2;
    while (i < n) {
      let j = Math.min(n - 1, i + 24);
      while (j > i && !this.lineClear(ax, ay, pts[j * 2], pts[j * 2 + 1])) j--;
      out.push(pts[j * 2], pts[j * 2 + 1]);
      ax = pts[j * 2];
      ay = pts[j * 2 + 1];
      i = j + 1;
    }
    return out;
  }

  /** Append a waypoint at the nearest point just outside a footprint rectangle. */
  private finalApproach(path: number[], sx: number, sy: number, rect: number[]): number[] {
    const ex = path.length ? path[path.length - 2] : sx;
    const ey = path.length ? path[path.length - 1] : sy;
    const m = (FX_ONE * 35) / 100;
    const px = Math.trunc(Math.min(Math.max(ex, rect[0] * FX_ONE - m), (rect[2] + 1) * FX_ONE + m));
    const py = Math.trunc(Math.min(Math.max(ey, rect[1] * FX_ONE - m), (rect[3] + 1) * FX_ONE + m));
    if (px !== ex || py !== ey) path.push(px, py);
    return path;
  }
}

const DX = [1, -1, 0, 0, 1, 1, -1, -1];
const DY = [0, 0, 1, -1, 1, -1, 1, -1];
